import { VolumeOffRounded, VolumeUpRounded } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import { SyntheticEvent, useEffect, useRef, useState } from 'react';

import { AnalyticsService, EVENTS, log } from '@/services';
import {
  selectIsPlayerPlaying,
  selectPersistedPlayerVolumePreference,
  useAppSelector,
} from '@/store';
import { EpisodeCard, Referrer } from '@/types';

import { episodeBannerStyles } from '../../styles';
import { BookmarkButton } from '../BookmarkButton';
import { PlayButton } from '../PlayButton';

type TrailerPreviewPlayerProps = {
  episode: EpisodeCard;
  referrer: Referrer;
  onEnded: () => void;
  onReady: () => void;
};
export const TrailerPreviewPlayer = ({
  episode,
  referrer,
  onEnded,
  onReady,
}: TrailerPreviewPlayerProps) => {
  const location = {
    ...referrer,
    variants: {
      ...referrer.variants,
      isTrailerPreviewOpen: true,
    },
  };

  const isPlayerPlaying = useAppSelector(selectIsPlayerPlaying);
  const volume = useAppSelector(selectPersistedPlayerVolumePreference);

  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    if (!videoRef.current) return;

    videoRef.current.volume = volume / 100;
  }, [volume]);

  const handleOnReady = () => {
    onReady();
    videoRef.current?.play();
    AnalyticsService.logEvent(EVENTS.EPISODE_EVENTS.TRAILER_PREVIEW_START, {
      episodeId: episode.episodeId,
    });
  };

  const handleOnError = (event: SyntheticEvent<HTMLVideoElement>) => {
    log(
      new Error('TrailerPreviewPlayer error', {
        cause: event.currentTarget.error,
      }),
    );

    onEnded();
  };

  const handleOnEnded = () => {
    onEnded();

    AnalyticsService.logEvent(EVENTS.EPISODE_EVENTS.TRAILER_PREVIEW_END, {
      episodeId: episode.episodeId,
    });
  };

  const toggleMute = (e: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (isMuted) {
      AnalyticsService.logEvent(EVENTS.EPISODE_EVENTS.TRAILER_PREVIEW_UNMUTE, {
        episodeId: episode.episodeId,
      });
    } else {
      AnalyticsService.logEvent(EVENTS.EPISODE_EVENTS.TRAILER_PREVIEW_MUTE, {
        episodeId: episode.episodeId,
      });
    }

    setIsMuted(!isMuted);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
      }}>
      <video
        ref={videoRef}
        src={episode.trailerPreviewUrl}
        width="100%"
        height="auto"
        onEnded={handleOnEnded}
        onError={handleOnError}
        disablePictureInPicture
        playsInline
        onCanPlay={handleOnReady}
        muted={isMuted || isPlayerPlaying}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          p: 3,
        }}>
        <PlayButton episode={episode} referrer={location} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isPlayerPlaying && (
            <IconButton
              sx={{ p: 0 }}
              disableRipple
              aria-label="Mute"
              onClick={toggleMute}>
              {isMuted ? (
                <VolumeOffRounded sx={episodeBannerStyles.bookmarkIcon} />
              ) : (
                <VolumeUpRounded sx={episodeBannerStyles.bookmarkIcon} />
              )}
            </IconButton>
          )}
          <BookmarkButton episodeId={episode.episodeId} referrer={location} />
        </Box>
      </Box>
    </Box>
  );
};
